// src/hooks/useDraggableScroll.js
// Updated: 10/20/2025 07:52 AM (Added Touch Event Support)

import { useRef, useEffect } from 'react';

export const useDraggableScroll = () => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let isDown = false;
    let startX;
    let startY;
    let scrollLeft;
    let scrollTop;

    // --- Mouse Event Handlers ---
    const mouseDownHandler = (e) => {
      // Prevent drag initiation if it's not the primary button (e.g., right-click)
      if (e.button !== 0) return;
      isDown = true;
      el.classList.add('grabbing');
      startX = e.pageX - el.offsetLeft;
      startY = e.pageY - el.offsetTop;
      scrollLeft = el.scrollLeft;
      scrollTop = el.scrollTop;
      // Prevent text selection during drag
      e.preventDefault();
    };

    const mouseLeaveHandler = () => {
      if (isDown) { // Only remove grabbing if actively dragging
          isDown = false;
          el.classList.remove('grabbing');
      }
    };

    const mouseUpHandler = (e) => {
        // Only act if it was the primary button that was released
      if (e.button !== 0) return;
      if (isDown) { // Only remove grabbing if actively dragging
          isDown = false;
          el.classList.remove('grabbing');
      }
    };

    const mouseMoveHandler = (e) => {
      if (!isDown) return;
      // No need to prevent default here usually, mouseDown already did for text selection
      const x = e.pageX - el.offsetLeft;
      const y = e.pageY - el.offsetTop;
      const walkX = (x - startX) * 2; // The scroll speed factor
      const walkY = (y - startY) * 2;
      el.scrollLeft = scrollLeft - walkX;
      el.scrollTop = scrollTop - walkY;
    };

    // --- Touch Event Handlers ---
    const touchStartHandler = (e) => {
        // Only initiate drag for single-touch events to allow pinch-zoom
        if (e.touches.length !== 1) {
            isDown = false; // Ensure dragging stops if multi-touch starts
            el.classList.remove('grabbing');
            return;
        }
        const touch = e.touches[0];
        isDown = true;
        el.classList.add('grabbing');
        startX = touch.pageX - el.offsetLeft;
        startY = touch.pageY - el.offsetTop;
        scrollLeft = el.scrollLeft;
        scrollTop = el.scrollTop;
        // Don't preventDefault here initially, allow potential pinch-zoom start
    };

    const touchEndHandler = () => {
      if (isDown) { // Only remove grabbing if actively dragging
          isDown = false;
          el.classList.remove('grabbing');
      }
    };

     const touchCancelHandler = () => {
      if (isDown) { // Ensure cleanup on cancel
          isDown = false;
          el.classList.remove('grabbing');
      }
    };

    const touchMoveHandler = (e) => {
      // Only scroll if dragging with a single touch
      if (!isDown || e.touches.length !== 1) return;

      // Prevent default scrolling/actions *only when* actively dragging with one finger
      e.preventDefault();

      const touch = e.touches[0];
      const x = touch.pageX - el.offsetLeft;
      const y = touch.pageY - el.offsetTop;
      const walkX = (x - startX) * 2; // Scroll speed factor
      const walkY = (y - startY) * 2;
      el.scrollLeft = scrollLeft - walkX;
      el.scrollTop = scrollTop - walkY;
    };


    // Add Mouse Listeners
    el.addEventListener('mousedown', mouseDownHandler);
    el.addEventListener('mouseleave', mouseLeaveHandler);
    el.addEventListener('mouseup', mouseUpHandler);
    el.addEventListener('mousemove', mouseMoveHandler);

    // Add Touch Listeners
    // Use { passive: false } for touchmove to allow preventDefault
    el.addEventListener('touchstart', touchStartHandler, { passive: true }); // Can be passive
    el.addEventListener('touchend', touchEndHandler);
    el.addEventListener('touchcancel', touchCancelHandler);
    el.addEventListener('touchmove', touchMoveHandler, { passive: false }); // Must NOT be passive to allow preventDefault

    // Cleanup function
    return () => {
      // Remove Mouse Listeners
      el.removeEventListener('mousedown', mouseDownHandler);
      el.removeEventListener('mouseleave', mouseLeaveHandler);
      el.removeEventListener('mouseup', mouseUpHandler);
      el.removeEventListener('mousemove', mouseMoveHandler);

      // Remove Touch Listeners
      el.removeEventListener('touchstart', touchStartHandler);
      el.removeEventListener('touchend', touchEndHandler);
      el.removeEventListener('touchcancel', touchCancelHandler);
      el.removeEventListener('touchmove', touchMoveHandler);
    };
  }, []); // Empty dependency array means this effect runs once on mount

  return ref;
};